import { formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EmployeesTrainingPlanService } from './services/employees-training-plan.service';
import { EmployeeDetails } from './models/employee-details';
import { MessageService, TreeNode } from 'primeng/api';
import { TreeData } from '../../shared/models/TreeData';
import { EqmsScopeManagementService } from '../../shared/services/eqms-scope-management.service';
import { AssignmentGroupInteractionService } from '../../system-admin/assignment-group/services/assignment-group-interaction.service';
import { SelectedUserScopesDTO } from './models/selectedUserScopesDTO';
import { DefaultValueConstants } from '../../shared/constants/default-value-constants';

@Component({
  selector: 'app-employees-training-plan',
  templateUrl: './employees-training-plan.component.html',
  styleUrls: ['./employees-training-plan.component.scss']
})

export class EmployeesTrainingPlanComponent implements OnInit {

  @Output() employeesTrainingPendingCount = new EventEmitter<number>(); 
  valueField: string = 'puid';
  labelField: string = 'name';
  employeeDetails: EmployeeDetails[] = [];
  selectedPuid: string | null = '';
  myEmployeesTab: boolean = false;
  puidSelected: boolean = false;
  isTrainingPlanSliderOpen: boolean = false;
  scopesTreeData: TreeNode[] = [];
  userScopes: SelectedUserScopesDTO | undefined;
  ifUserIsSelected : boolean = false;
  userPUID: string | undefined;
  isReviewButtonEnable = false;
  treeData: TreeNode[] = [];
  selectedUser: TreeNode | null = null;
  userWithPendingStatusSelected: boolean = false;
  userWithReviewUpToDateSelected: boolean = false;
  userWithTerminatedStatusSelected: boolean = false;
  isTrainingPlanReviewHistoryModalOpen = false;
  enableTrainingPlanReviewHistoryBtn = false;
  enableAddTrainingPlanReviewBtn = false;
  employees: EmployeeDetails[] = [];
  
  constructor(private employeesTrainingPlanService: EmployeesTrainingPlanService,
      private eqmsScopeManagementService:EqmsScopeManagementService,
      private assignmentGroupInteractionService: AssignmentGroupInteractionService,
      private messageService: MessageService){
    }

  ngOnInit(): void {
    this.employeesTrainingPlanService.clearProgressKPI();
    this.loadEmployeeDetails();
  } 

  handleTreeSelectChange(event: any) {
    // Checking the selected value has status as pending
    if (event && event.node && event.node.parent.label === DefaultValueConstants.pendingReview_label) {
      this.userWithPendingStatusSelected = true; 
      this.userWithReviewUpToDateSelected = false;
      this.userWithTerminatedStatusSelected = false;
    }
    else if(event && event.node && event.node.parent.label === DefaultValueConstants.terminatedUsers_label){
      this.userWithPendingStatusSelected = false; 
      this.userWithReviewUpToDateSelected = false;
      this.userWithTerminatedStatusSelected = true;
    }
    else {
      this.userWithPendingStatusSelected = false; 
      this.userWithReviewUpToDateSelected = true;
      this.userWithTerminatedStatusSelected = false;
    }

  //checking the selected value is not null - event.node.parent.label is not null check
    this.enableAddTrainingPlanReviewBtn = !!event.node.parent.label && !this.userWithTerminatedStatusSelected;
    this.enableTrainingPlanReviewHistoryBtn = !!event.node.parent.label;

   //Get the puid from the inpit
    if (event && event.node && event.node.label) {
        this.selectedPuid = this.extractPUID(event.node.label);
        this.userPUID = this.selectedPuid;
        this.puidChange(); // to get linked, Ancestor & Decendant Scopes for validation
      } else {
        this.selectedPuid = null;
      }    
  }
  
  loadEmployeeDetails = () => {
    const managerPuid = sessionStorage.getItem('puid') || '';
    this.employeesTrainingPlanService.getEmployeeDetailsForManager(managerPuid).subscribe((employeeDetails) => {
      this.getEmployeesTrainingPendingCount(employeeDetails);
      this.treeData = this.createEmployeeDetailsTreeNodes(employeeDetails);
      this.employees = employeeDetails;
    });
  }

  getEmployeesTrainingPendingCount(employeeDetails: EmployeeDetails[]): void {
    const pendingEmployees = employeeDetails.filter(emp => emp.status === DefaultValueConstants.pending_label).length;
    this.employeesTrainingPendingCount.emit(pendingEmployees);
  }

  createEmployeeDetailsTreeNodes(employeeDetails: EmployeeDetails[]): TreeNode[] {
    let pendingReviewNode: TreeNode = {
      label: DefaultValueConstants.pendingReview_label,
      selectable: false,
      children: [],
    };
  
    let reviewUpToDateNode: TreeNode = {
      label: DefaultValueConstants.reviewUpToDate_label,
      selectable: false,
      children: [],
    };
  
    let terminatedUsersNode: TreeNode = {
      label: DefaultValueConstants.terminatedUsers_label,
      selectable: false,
      children: [],
    };
  
    employeeDetails.forEach(employee => {
      let nodeLabel = employee.name;
      let formattedDate = this.formatDateToString(employee.dueDate);
      const fullLabel = nodeLabel + (formattedDate ? ` (${formattedDate})` : '');
    
      const commonNodeData: Partial<TreeNode> = {
        label: fullLabel,
        data: employee
      };
    
      if (employee.status === DefaultValueConstants.pending_label) {
        pendingReviewNode.children!.push({
          ...commonNodeData,
          styleClass: 'pending-user-node'
        });
      } else if (employee.status === DefaultValueConstants.terminated_label) {
        terminatedUsersNode.children!.push({
          ...commonNodeData,
          styleClass: 'terminated-user-node'
        });
      } else {
        reviewUpToDateNode.children!.push({
          ...commonNodeData,
          styleClass: 'reviewed-user-node'
        });
      }
    });
  
    return [pendingReviewNode, reviewUpToDateNode, terminatedUsersNode];
  }

  formatDateToString(date: Date | null | undefined): string {
    // Return empty string if date is null or undefined or invalid
    if (!date) return ''; 
    return formatDate(date, 'dd-MMM-yyyy', 'en-US');
  }
  
  // to get linked, Ancestor & Decendant Scopes for validation. passed as input parameter later
  puidChange(){
    this.myEmployeesTab = true;
    this.puidSelected = this.selectedPuid != null ? true : false;
      if(this.selectedPuid != null){
        this.employeesTrainingPlanService.getSelectedUserScopes(this.selectedPuid).subscribe((value) => {
          this.userScopes = value;
        });
      }
    }

    loadEQMSScope = () => {
      this.eqmsScopeManagementService.getEQMSScope().subscribe((eQMSScope) => {
        this.scopesTreeData.push(this.createTreeNode(eQMSScope));
      });
    }   
    
    reviewUserTrainingPlanReview(){
      this.isReviewButtonEnable = true;
    }
    
    closeReasonDailog(value : boolean){    
      this.isReviewButtonEnable = value
    }

    private createTreeNode(node: any): TreeNode {
      const isNodeEnabled = node.isEnable; 
      return {
        label: node.eQMSScopeName,
        data: node,
        expanded: true,
        selectable: isNodeEnabled,
        children: node.children ? this.createChildNodes(node.children) : undefined,
        style: isNodeEnabled ? 'color: #000000' : 'color: #888'
      };
    }

    private extractPUID(node: string) {
      const startIndex = node.indexOf('(') + 1;
      const endIndex = node.indexOf(')', startIndex);
      return node.substring(startIndex, endIndex);
    }  
    
    private createChildNodes(nodes: any[]): TreeNode[] {
      return nodes.map(childNode => this.createTreeNode(childNode));
    }
    
    checkIfUserIsSelected(){
      this.ifUserIsSelected = true;
      this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
    }

    closeTrainingPlanSlider(isSliderActive : boolean){
      this.isTrainingPlanSliderOpen = isSliderActive;
    }
    
    getTrainingPlanSlider(){
      this.isTrainingPlanSliderOpen = !this.isTrainingPlanSliderOpen;
    }

    closeTrainingPlanReviewHistoryModal(isOpen: boolean){
      this.isTrainingPlanReviewHistoryModalOpen = false;
    }

    getTrainingPlanReviewHistory(){
      this.isTrainingPlanReviewHistoryModalOpen = !this.isTrainingPlanReviewHistoryModalOpen;
    }
    
    exportToExcel() {
      if (this.employees.some(x => x.status === DefaultValueConstants.pending_label)) {
        var puid = sessionStorage['puid'] != null ? sessionStorage['puid'] : null;
        this.employeesTrainingPlanService.exportOutstandingReviewReport(puid);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'You don\'t have any user with pending Training Plan Review.',
        });
        
      }
      
    }

}
